/**
 * Quote templates — the Company's named quote section presets (#188
 * follow-on), rendered through the shared roster and studio shell (#241).
 * This file is the quote's half: its roster copy, its four API calls, the
 * quote/invoice face switch (the one thing no other artifact has), and the
 * six company-wide values that edit as fields beside the sheet. The toggles
 * shape deposit and balance invoices too.
 */
import { useState } from "react";
import { Select } from "../../components/ui/Select";
import forms from "../../components/ui/field.module.css";
import type {
  CompanySettings,
  CompanySettingsUpdate,
} from "../../lib/company-settings-api";
import {
  documentPreviewRequest,
  getDocumentPreview,
} from "../../lib/document-preview-api";
import {
  createQuoteTemplate,
  deleteQuoteTemplate,
  fullInvoiceSections,
  fullQuoteSections,
  listQuoteTemplates,
  updateQuoteTemplate,
  type InvoiceSections,
  type QuoteSections,
  type QuoteTemplate,
} from "../../lib/quote-templates-api";
import { previewKeyOf, usePreview } from "./use-preview";
import { FieldLabel, numOrNull } from "./section-chrome";
import {
  sectionCount,
  TemplateRoster,
  type RosterArtifact,
} from "./TemplateRoster";
import {
  httpUrlOr,
  StudioShell,
  useSettingFields,
  useTemplateStudio,
} from "./studio-shell";
import styles from "./QuoteTemplatesSection.module.css";

const QUOTE_ROSTER: RosterArtifact<QuoteTemplate> = {
  idPrefix: "quote",
  title: "Quote",
  description:
    "The priced quote customers receive and approve. Each template picks which sections quotes and invoices include.",
  defaultDescription:
    "Bookings use this template unless the Booking picks another. It also shapes deposit and balance invoices.",
  floorLabel: "Full quote",
  emptyText: "No templates yet. Customers receive the full quote.",
  deleteWarning: "Bookings using it must be moved to another template first.",
  countLine: (t) =>
    `${sectionCount(t.sections)} quote · ${sectionCount(t.invoice_sections)} invoice sections`,
  list: listQuoteTemplates,
  create: (name) =>
    createQuoteTemplate({
      name,
      sections: fullQuoteSections(),
      invoice_sections: fullInvoiceSections(),
    }),
  remove: deleteQuoteTemplate,
  setDefault: (t, isDefault) =>
    updateQuoteTemplate(t.id, {
      name: t.name,
      sections: t.sections,
      invoice_sections: t.invoice_sections,
      is_default: isDefault,
    }),
};

export function QuoteTemplatesSection({
  settings,
  onPatch,
  onBack,
}: {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  /** Returns to the Documents & emails gallery from the templates list. */
  onBack?: () => void;
}) {
  return (
    <TemplateRoster
      artifact={QUOTE_ROSTER}
      onBack={onBack}
      renderStudio={(studio) => (
        <QuoteTemplateStudio
          key={studio.template.id}
          settings={settings}
          onPatch={onPatch}
          {...studio}
        />
      )}
    />
  );
}

/* ─── Template editor ───────────────────────────────────────────────────── */

/** A quote template's two faces: one section map each, saved together. */
interface QuoteDraft {
  sections: QuoteSections;
  invoice_sections: InvoiceSections;
}

/**
 * The quote's studio: the shared shell plus the one thing only this artifact
 * has — a face switch, because a quote template also shapes the deposit and
 * balance invoices. The company-wide values stay labelled fields beside the
 * sheet; a number mid-sentence edited in place would be undiscoverable
 * (DESIGN.md "Settings studios").
 */
function QuoteTemplateStudio({
  template,
  settings,
  onPatch,
  onSaved,
  onBack,
}: {
  template: QuoteTemplate;
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  onSaved: (next: QuoteTemplate) => void;
  onBack: () => void;
}) {
  // face: which side of the template is on the sheet. invoiceFace is
  // view-only — deposit and balance share the one invoice section set.
  const [face, setFace] = useState<"quote" | "invoice">("quote");
  const [invoiceFace, setInvoiceFace] = useState<"deposit" | "balance">(
    "deposit",
  );

  // Side-field drafts for the company-wide scalar values.
  const [depositPercent, setDepositPercent] = useState(
    String(settings.deposit_percent_default),
  );
  const [validDays, setValidDays] = useState(
    String(settings.quote_valid_days_default),
  );
  const [extraDayPercent, setExtraDayPercent] = useState(
    String(settings.multi_day_extra_day_percent),
  );
  const [termsUrl, setTermsUrl] = useState(settings.terms_url);
  const [paymentTerms, setPaymentTerms] = useState(settings.payment_terms_text);
  const [extraNotes, setExtraNotes] = useState(settings.extra_notes_text);
  const { commit, fieldError } =
    useSettingFields<CompanySettingsUpdate>(onPatch);

  // Each scalar is valid on its own terms; an out-of-range draft is refused
  // at the field, never sent.
  const depositDraft = numOrNull(depositPercent);
  const daysDraft = numOrNull(validDays);
  const extraDayDraft = numOrNull(extraDayPercent);
  const deposit =
    depositDraft !== null && depositDraft <= 100 ? depositDraft : null;
  const days = daysDraft !== null && daysDraft >= 1 ? daysDraft : null;
  const extraDay =
    extraDayDraft !== null && extraDayDraft <= 100 ? extraDayDraft : null;

  const studio = useTemplateStudio<QuoteTemplate, QuoteDraft>({
    template,
    initialSections: {
      sections: template.sections,
      invoice_sections: template.invoice_sections,
    },
    activeSections: (draft) =>
      face === "invoice" ? draft.invoice_sections : draft.sections,
    toggleSection: (draft, key, next) =>
      face === "invoice"
        ? {
            ...draft,
            invoice_sections: { ...draft.invoice_sections, [key]: next },
          }
        : { ...draft, sections: { ...draft.sections, [key]: next } },
    save: (name, draft) =>
      updateQuoteTemplate(template.id, {
        name,
        sections: draft.sections,
        invoice_sections: draft.invoice_sections,
      }),
    onSaved,
    docClass: "quote-doc",
  });

  // A draft the server would refuse previews as the saved value, so the
  // preview never depends on a half-typed field being finished.
  const values = {
    deposit_percent_default: deposit ?? settings.deposit_percent_default,
    quote_valid_days_default: days ?? settings.quote_valid_days_default,
    multi_day_extra_day_percent:
      extraDay ?? settings.multi_day_extra_day_percent,
    terms_url: httpUrlOr(termsUrl, settings.terms_url),
    payment_terms_text: paymentTerms,
    extra_notes_text: extraNotes,
  };

  const request =
    face === "invoice"
      ? documentPreviewRequest(settings, "invoice", {
          ...values,
          format: "html",
          invoice_sections: studio.draft.invoice_sections,
          invoice_face: invoiceFace,
          quote_template_name: template.name,
        })
      : documentPreviewRequest(settings, "quote", {
          ...values,
          format: "html",
          quote_sections: studio.draft.sections,
          quote_template_name: template.name,
        });

  const {
    result,
    loading,
    error: previewError,
  } = usePreview(previewKeyOf(request), (signal) =>
    getDocumentPreview({ ...request, signal }),
  );

  return (
    <StudioShell
      backLabel="All quote templates"
      onBack={onBack}
      savedName={template.name}
      name={studio.name}
      description={
        face === "invoice"
          ? "The deposit and balance invoices Customers on this template receive."
          : "The quote Customers on this template receive."
      }
      saveError={studio.saveError}
      previewError={previewError}
      fieldsNote="These values apply to every quote, whichever template."
      fields={
        <>
          <div className={styles.pairedFields}>
            <div>
              <FieldLabel
                htmlFor="quote-deposit-percent"
                tooltip="The share of the total invoiced as the deposit when the Customer approves."
              >
                Deposit %
              </FieldLabel>
              <input
                id="quote-deposit-percent"
                type="number"
                min={0}
                max={100}
                className={forms.input}
                value={depositPercent}
                onChange={(e) => setDepositPercent(e.target.value)}
                onBlur={() =>
                  void commit(
                    "deposit",
                    "deposit_percent_default",
                    deposit,
                    settings.deposit_percent_default,
                    "Enter 0–100.",
                  )
                }
              />
              {fieldError("deposit")}
            </div>
            <div>
              <FieldLabel htmlFor="quote-valid-days">
                Expires after (days)
              </FieldLabel>
              <input
                id="quote-valid-days"
                type="number"
                min={1}
                className={forms.input}
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                onBlur={() =>
                  void commit(
                    "days",
                    "quote_valid_days_default",
                    days,
                    settings.quote_valid_days_default,
                    "Enter at least 1.",
                  )
                }
              />
              {fieldError("days")}
            </div>
          </div>
          <div>
            <FieldLabel
              htmlFor="quote-extra-day-percent"
              tooltip="Each extra hire day is charged this percent of the first day's gear rate."
            >
              Additional hire days %
            </FieldLabel>
            <input
              id="quote-extra-day-percent"
              type="number"
              min={0}
              max={100}
              className={forms.input}
              value={extraDayPercent}
              onChange={(e) => setExtraDayPercent(e.target.value)}
              onBlur={() =>
                void commit(
                  "extraDay",
                  "multi_day_extra_day_percent",
                  extraDay,
                  settings.multi_day_extra_day_percent,
                  "Enter 0–100.",
                )
              }
            />
            {fieldError("extraDay")}
          </div>
          <div>
            <FieldLabel
              htmlFor="quote-terms-url"
              tooltip="Added as a link on the Quote so Customers can read your terms."
            >
              Your terms and conditions web page
            </FieldLabel>
            <input
              id="quote-terms-url"
              type="url"
              className={forms.input}
              value={termsUrl}
              onChange={(e) => setTermsUrl(e.target.value)}
              onBlur={() =>
                void commit(
                  "termsUrl",
                  "terms_url",
                  termsUrl,
                  settings.terms_url,
                )
              }
            />
            {fieldError("termsUrl")}
          </div>
          <div>
            <FieldLabel
              htmlFor="quote-payment-terms"
              tooltip="A short sentence explaining when the deposit or balance must be paid."
            >
              Payment timing shown on Quotes
            </FieldLabel>
            <textarea
              id="quote-payment-terms"
              rows={3}
              className={forms.input}
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              onBlur={() =>
                void commit(
                  "paymentTerms",
                  "payment_terms_text",
                  paymentTerms,
                  settings.payment_terms_text,
                )
              }
            />
            {fieldError("paymentTerms")}
          </div>
          <div>
            <FieldLabel
              htmlFor="quote-extra-notes"
              tooltip="A standing note shown on every quote (as a Notes block) and every job sheet."
            >
              Extra notes
            </FieldLabel>
            <textarea
              id="quote-extra-notes"
              rows={3}
              className={forms.input}
              value={extraNotes}
              onChange={(e) => setExtraNotes(e.target.value)}
              onBlur={() =>
                void commit(
                  "extraNotes",
                  "extra_notes_text",
                  extraNotes,
                  settings.extra_notes_text,
                )
              }
            />
            {fieldError("extraNotes")}
          </div>
        </>
      }
      toolbar={
        /* Face switcher: which of the template's two documents is on the
           sheet. Instant swap, no motion; the deposit/balance select is
           view-only — both invoice faces share the one section set. */
        <div className={styles.faceBar}>
          <div className={styles.faces}>
            <button
              type="button"
              onClick={() => setFace("quote")}
              className={face === "quote" ? styles.faceSelected : styles.face}
            >
              Quote
            </button>
            <button
              type="button"
              onClick={() => setFace("invoice")}
              className={
                face === "invoice" ? styles.faceSelected : styles.face
              }
            >
              Invoices
            </button>
          </div>
          {face === "invoice" && (
            <Select
              variant="quiet"
              ariaLabel="Previewed invoice"
              value={invoiceFace}
              onChange={(next) => setInvoiceFace(next as "deposit" | "balance")}
              options={[
                { value: "deposit", label: "Deposit invoice" },
                { value: "balance", label: "Balance invoice" },
              ]}
            />
          )}
        </div>
      }
      hiddenCSS={studio.hiddenCSS}
      result={result}
      loading={loading}
      onSheetClick={studio.handleSheetClick}
    />
  );
}
